<template>
  <div class='wrapper-node-properties'>
    <div class='wrapper-node-properties__header'>
      <div>
        <v-icon>inventory</v-icon>
      </div>
      <div class='wrapper-node-properties__header__title'>
        <div class='wrapper-node-properties__header__title__name'>
          {{name}}
        </div>
        <div class='wrapper-node-properties__header__title__type'>
          {{$t('ctc.properties.type.container')}}
        </div>
      </div>
    </div>
    <v-expansion-panels multiple v-model="panels" flat>
      <v-expansion-panel>
        <v-expansion-panel-header class='panel-header'>
          {{data.type === propertyTypes.subdomains_wrapper ? $t('ctc.properties.filters.subdomains.title') : $t('ctc.properties.filters.title')}}
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="filters-content">
            <div class="filters-list">
              <div v-for="(filter, index) in filters" :key="index" class="filter-row">
                <v-text-field
                  v-model="filters[index]"
                  solo
                  dense
                  outlined
                  @change="updateFilters"
                  class="filter-input"
                  :placeholder="($t('ctc.properties.filters.label') + ' #' + (index+1)).toUpperCase()"
                />
                <custom-tooltip popper-class="light-tooltip" placement="right">
                  <div slot='buttonContent'>
                    <span
                      class="delete-icon material-icons"
                      :title="$t('delete')"
                      @click="removeFilter(index)"
                    >
                      highlight_off
                    </span>
                  </div>
                  <div slot="content">
                    {{$t('delete')}}
                  </div>
                </custom-tooltip>
              </div>
            </div>
            <div class="add-new-filter-section">
              <div class="add-new-filter-header">
                <label class="add-new-filter-button" @click="addFilter">
                  <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
                </label>
              </div>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel v-if="data.type === propertyTypes.subdomains_wrapper">
        <v-expansion-panel-header class='panel-header'>{{$t('ctc.properties.filters.ip.title')}}</v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="filters-content">
            <div class="filters-list">
              <div v-for="(filter, index) in ipFilters" :key="index" class="filter-row">
                <v-text-field
                  v-model="ipFilters[index]"
                  solo
                  dense
                  outlined
                  @change="updateFilters"
                  class="filter-input"
                  :placeholder="($t('ctc.properties.filters.label') + ' #' + (index+1)).toUpperCase()"
                />
                <custom-tooltip popper-class="light-tooltip" placement="right">
                  <div slot='buttonContent'>
                    <span
                      class="delete-icon material-icons"
                      :title="$t('delete')"
                      @click="removeIpFilter(index)"
                    >
                      highlight_off
                    </span>
                  </div>
                  <div slot="content">
                    {{$t('delete')}}
                  </div>
                </custom-tooltip>
              </div>
            </div>
            <div class="add-new-filter-section">
              <div class="add-new-filter-header">
                <label class="add-new-filter-button" @click="addIpFilter">
                  <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
                </label>
              </div>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { extendedPropertyTypes } from '@/constants/threatCanvas';
import CustomTooltip from '@/components/common/CustomTooltip.vue';

export default {
  name: 'WrapperNodeProperties',
  components: {
    CustomTooltip,
  },
  props: {
    data: {
      type: Object,
      required: false,
      default: () => ({
        type: '',
        name: '',
        filters: [],
        ipFilters: [],
      }),
    },
  },
  data() {
    return {
      panels: [0, 1],
      filters: [],
      ipFilters: [],
    };
  },
  computed: {
    name() {
      if (this.data.type === extendedPropertyTypes.email_wrapper) {
        return this.$t('ctc.properties.wrapper.name.email');
      }
      if (this.data.type === extendedPropertyTypes.subdomains_wrapper) {
        return this.$t('ctc.properties.wrapper.name.subdomain');
      }
      return '';
    },
    propertyTypes() {
      return extendedPropertyTypes;
    },
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(newData) {
        if (newData) {
          this.filters = newData.filters ? [...newData.filters].filter(filter => filter.trim() !== '') : [];
          this.ipFilters = newData.ipFilters ? [...newData.ipFilters].filter(filter => filter.trim() !== '') : [];
        } else {
          this.filters = [];
          this.ipFilters = [];
        }
      },
    },
  },
  methods: {
    updateFilters() {
      this.$emit('property-changed', {
        type: this.data.type,
        filters: [...this.filters],
        ipFilters: [...this.ipFilters],
      });
    },
    addFilter() {
      this.filters.push('');
      this.updateFilters();
    },
    removeFilter(index) {
      if (this.filters.length > 1) {
        this.filters.splice(index, 1);
      } else {
        this.filters = [''];
      }
      this.updateFilters();
    },
    addIpFilter() {
      this.ipFilters.push('');
      this.updateFilters();
    },
    removeIpFilter(index) {
      if (this.ipFilters.length > 1) {
        this.ipFilters.splice(index, 1);
      } else {
        this.ipFilters = [''];
      }
      this.updateFilters();
    },
  },
};
</script>

<style scoped lang='scss'>
@import '~@/assets/styles/design-system';
.wrapper-node-properties {
  display: flex;
  flex-direction: column;
  margin-top: rem(15);

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-left: rem(10);

    &__title {
      display: flex;
      flex-direction: column;
      margin-left: rem(15);

      &__name {
        color: var(--text-primary);
        font-family: $font-family-regular;
        font-size: rem(16);
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(17);
      }

      &__type {
        color: var(--text-tertiary);
        font-family: $font-family-regular;
        font-size: rem(10);
        letter-spacing: 1.1px;
        line-height: rem(15);
        margin-top: rem(5);
        text-transform: uppercase;
      }
    }
  }

  .panel-header {
    color: var(--text-primary);
    font-family: $font-family-medium;
    font-size: rem(14);
    letter-spacing: 0;
    line-height: rem(17);
    text-transform: uppercase;
  }

  .filters-content {
    padding: rem(10);

    .filters-list {
      margin-bottom: rem(10);

      .filter-row {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        gap: rem(8);
        margin-bottom: rem(8);

        .filter-input {
          // match prod's row height — Vuetify reserves hint space below input
          padding-bottom: 18px;
        }

        .delete-icon {
          color: var(--status-error);
          cursor: pointer;
          font-size: rem(16);
          font-weight: bolder;
          // offset up to align with input box center, not full row center
          margin-bottom: 18px;
        }
      }
    }

    .add-new-filter-section {
      padding: 0;

      .add-new-filter-header {
        display: flex;
        justify-content: space-between;

        .add-new-filter-button {
          color: var(--brand-primary);
          text-transform: uppercase;
          font-size: rem(12);
          font-weight: 500;
          letter-spacing: 1.07px;
          line-height: rem(16);
          text-align: center;
          margin-right: rem(12);
          cursor: pointer;

          .plus {
            font-size: rem(16);
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
// Override Vuetify's dark theme expansion panel background for this component
.wrapper-node-properties {
  .theme--dark.v-expansion-panels .v-expansion-panel {
    background-color: transparent;
  }
}
</style>
